import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Linkedin, Github, Twitter, Globe, Facebook, Instagram, Youtube, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { SocialLink } from '../types';

interface SocialLinksSectionProps {
  socialLinks: SocialLink[];
  onAddLink: (platform: string, url: string) => Promise<void>;
  onUpdateLink: (index: number, link: SocialLink) => Promise<void>;
  onDeleteLink: (index: number) => Promise<void>;
  onMoveLink: (index: number, direction: 'up' | 'down') => void;
  isFieldAllowed: (field: string) => boolean;
  disabled?: boolean;
  t: (key: string) => string;
}

const platformOptions = [
  { value: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/yourprofile', color: 'text-[#0A66C2]' },
  { value: 'GitHub', icon: Github, placeholder: 'https://github.com/username', color: 'text-gray-800' },
  { value: 'Twitter', icon: Twitter, placeholder: 'https://twitter.com/handle', color: 'text-[#1DA1F2]' },
  { value: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/profile', color: 'text-[#1877F2]' },
  { value: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/handle', color: 'text-[#E4405F]' },
  { value: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@channel', color: 'text-[#FF0000]' },
  { value: 'Website', icon: Globe, placeholder: 'https://yourwebsite.com', color: 'text-green-600' },
];

const getPlatformIcon = (platform: string) => {
  const option = platformOptions.find(p => p.value.toLowerCase() === platform.toLowerCase());
  if (option) {
    const Icon = option.icon;
    return <Icon className={`w-5 h-5 ${option.color}`} />;
  }
  return <Globe className="w-5 h-5 text-muted-foreground" />;
};

const SocialLinksSection: React.FC<SocialLinksSectionProps> = ({
  socialLinks,
  onAddLink,
  onUpdateLink,
  onDeleteLink,
  onMoveLink,
  isFieldAllowed,
  disabled = false,
  t,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newPlatform, setNewPlatform] = useState('LinkedIn');
  const [newUrl, setNewUrl] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const isAllowed = isFieldAllowed('socialLinks');

  if (!isAllowed) {
    return (
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">{t('Social Links')}</h3>
          <p className="text-sm text-muted-foreground">{t('Social links are not enabled for your profile type.')}</p>
        </CardContent>
      </Card>
    );
  }

  const handleAdd = async () => {
    if (!newUrl.trim()) return;

    setIsSubmitting(true);
    try {
      await onAddLink(newPlatform, newUrl.trim());
      setNewUrl('');
      setNewPlatform('LinkedIn');
      setIsAdding(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (index: number) => {
    if (!editUrl.trim()) return;

    const link = socialLinks[index];
    setIsSubmitting(true);
    try {
      await onUpdateLink(index, { ...link, url: editUrl.trim() });
      setEditingIndex(null);
      setEditUrl('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (index: number) => {
    setDeletingIndex(index);
    try {
      await onDeleteLink(index);
    } finally {
      setDeletingIndex(null);
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditUrl(socialLinks[index].url);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditUrl('');
  };

  const getPlaceholder = (platform: string) => {
    const option = platformOptions.find(p => p.value === platform);
    return option?.placeholder || 'https://example.com/yourprofile';
  };

  // Filter out platforms that already have a link
  const availablePlatforms = platformOptions.filter(
    p => !socialLinks.some(link => link.platform.toLowerCase() === p.value.toLowerCase())
  );

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">{t('Social Links')}</h3>
          {!isAdding && availablePlatforms.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setNewPlatform(availablePlatforms[0].value);
                setIsAdding(true);
              }}
              disabled={disabled}
              className="text-primary gap-1.5"
            >
              <Plus className="w-4 h-4" />
              {t('Add Link')}
            </Button>
          )}
        </div>

        {socialLinks.length === 0 && !isAdding ? (
          <div className="text-center py-8 bg-muted rounded-lg border-2 border-dashed border-border">
            <Globe className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-3">{t('No social links added yet')}</p>
            {availablePlatforms.length > 0 && (
              <Button
                onClick={() => {
                  setNewPlatform(availablePlatforms[0].value);
                  setIsAdding(true);
                }}
                disabled={disabled}
                className="gap-1.5"
              >
                <Plus className="w-4 h-4" />
                {t('Add your first social link')}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Existing Links */}
            {socialLinks.map((link, index) => (
              <div
                key={link.id || index}
                className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border group"
              >
                {/* Platform Icon */}
                <div className="flex-shrink-0">
                  {getPlatformIcon(link.platform)}
                </div>

                {/* Content */}
                {editingIndex === index ? (
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="url"
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      placeholder={getPlaceholder(link.platform)}
                      className="flex-1 px-3 py-1.5 text-sm border border-input rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      disabled={isSubmitting}
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={() => handleUpdate(index)}
                      disabled={isSubmitting || !editUrl.trim()}
                    >
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Save')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={isSubmitting}
                    >
                      {t('Cancel')}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{link.platform}</p>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:text-primary/80 truncate block"
                      >
                        {link.url}
                      </a>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => window.open(link.url, '_blank')}
                        className="p-1.5 text-muted-foreground hover:text-primary rounded"
                        title={t('Open link')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => startEditing(index)}
                        disabled={disabled}
                        className="p-1.5 text-muted-foreground hover:text-foreground rounded disabled:opacity-50"
                        title={t('Edit')}
                      >
                        <span className="text-xs font-medium">{t('Edit')}</span>
                      </button>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => onMoveLink(index, 'up')}
                          disabled={disabled}
                          className="p-1.5 text-muted-foreground hover:text-foreground rounded disabled:opacity-50"
                          title={t('Move up')}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                      )}
                      {index < socialLinks.length - 1 && (
                        <button
                          type="button"
                          onClick={() => onMoveLink(index, 'down')}
                          disabled={disabled}
                          className="p-1.5 text-muted-foreground hover:text-foreground rounded disabled:opacity-50"
                          title={t('Move down')}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        disabled={disabled || deletingIndex === index}
                        className="p-1.5 text-muted-foreground hover:text-destructive rounded disabled:opacity-50"
                        title={t('Delete')}
                      >
                        {deletingIndex === index ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Add New Link Form */}
            {isAdding && (
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm font-medium text-foreground mb-3">{t('Add Social Link')}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <select
                    value={newPlatform}
                    onChange={(e) => setNewPlatform(e.target.value)}
                    disabled={isSubmitting}
                    className="px-3 py-2 text-sm border border-input rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card"
                  >
                    {availablePlatforms.map(platform => (
                      <option key={platform.value} value={platform.value}>
                        {platform.value}
                      </option>
                    ))}
                  </select>
                  <input
                    type="url"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder={getPlaceholder(newPlatform)}
                    disabled={isSubmitting}
                    className="flex-1 px-3 py-2 text-sm border border-input rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newUrl.trim()) {
                        handleAdd();
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAdd}
                      disabled={isSubmitting || !newUrl.trim()}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('Add')
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsAdding(false);
                        setNewUrl('');
                      }}
                      disabled={isSubmitting}
                    >
                      {t('Cancel')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {socialLinks.length >= 7 && (
          <p className="mt-3 text-xs text-muted-foreground">
            {t('Maximum number of social links reached')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SocialLinksSection;
